import React, { useState, useRef, useEffect } from "react";
import useWindowSize from "hooks/useWindowResize";
import MenuItemDirect from "./MenuItemDirect";

const MenuListDirect = ({
  menuCategories,
  menuItems,
  selectedCategoryId,
  setSelectedCategoryId,
  setModalItem,
  expandedCategory,
  selectedValues = [],
  operatorId, // ✅ Add this line
  operatorType,
  operatorSlug,
}) => {
  const [selectedId, setSelectedId] = useState([]);
  const [totalTime, setTotalTime] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const categoryRefs = useRef({});
  const categoryButtonRefs = useRef({});

  const groupedItems = menuCategories.reduce((acc, cat) => {
    acc[cat._id] = menuItems.filter((item) => item.category === cat._id);
    return acc;
  }, {});

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const categoryId = entry.target.getAttribute("data-category-id");
            if (categoryId) {
              setSelectedCategoryId(categoryId);

              const button = categoryButtonRefs.current?.[categoryId];
              if (button) {
                button.scrollIntoView({
                  behavior: "smooth",
                  inline: "start",
                  block: "nearest",
                });
              }
            }
          }
        });
      },
      { threshold: 0.4 }
    );

    Object.keys(categoryRefs.current).forEach((id) => {
      if (categoryRefs.current[id]) {
        observer.observe(categoryRefs.current[id]);
      }
    });

    return () => observer.disconnect();
  }, [menuCategories]);

  useEffect(() => {
    const total = selectedValues.reduce((acc, item) => {
      const desc = item.desc?.toLowerCase() || "";
      const hourMatch = desc.match(/(\d+)\s*hr/);
      const minMatch = desc.match(/(\d+)\s*min/);
      const hours = hourMatch ? parseInt(hourMatch[1], 10) : 0;
      const mins = minMatch ? parseInt(minMatch[1], 10) : 0;
      return acc + hours * 60 + mins;
    }, 0);
    setTotalTime(total);
    setSelectedId(selectedValues.map((v) => v._id));
  }, [selectedValues]);

  let WIN_WIDTH = useWindowSize().width;
  if (typeof window !== "undefined") {
    WIN_WIDTH = WIN_WIDTH || window.innerWidth;
  }

  const highlightMatch = (text, keyword) => {
    if (!keyword) return text;
    const regex = new RegExp(`(${keyword})`, "gi");
    return text.split(regex).map((part, i) =>
      part.toLowerCase() === keyword.toLowerCase() ? (
        <mark key={i} className="bg-redlight text-reddark">
          {part}
        </mark>
      ) : (
        <span key={i}>{part}</span>
      )
    );
  };

  return (
    <div className="w-full mx-auto p-0 sm:p-0 flex flex-col lg:flex-row gap-6">
      {/* LEFT SIDE - Services */}
      <div className="w-full">
        {/* Fixed top area */}
        <div className="sticky -top-1 z-10 bg-[#0d1124] pb-4">
          <h2 className="text-2xl font-semibold mb-4 text-white">
            {operatorType === "ordering" ? "Food Menu" : "Services"}
          </h2>
          <div className="flex overflow-x-auto space-x-3 mb-4 pb-2 scrollbar-thin">
            {menuCategories
              .filter((cat) =>
                groupedItems[cat._id].some((item) =>
                  item.title.toLowerCase().includes(searchTerm.toLowerCase())
                )
              )
              .map((cat) => (
                <button
                  key={cat._id}
                  ref={(el) => (categoryButtonRefs.current[cat._id] = el)}
                  onClick={() => {
                    const section = categoryRefs.current[cat._id];
                    if (section) section.scrollIntoView({ behavior: "smooth" });
                  }}
                  className={`whitespace-nowrap uppercase px-4 py-2 rounded-full text-sm font-semibold border border-border ${
                    selectedCategoryId === cat._id
                      ? "bg-redlight text-white"
                      : "bg-dry text-textGray"
                  }`}
                >
                  {cat.name}
                </button>
              ))}
          </div>
        </div>

        {/* Scrollable service list below fixed header */}
        <div className="pt-4 scrollbar-thin">
          {menuCategories.map((cat) => {
            const filteredItems = groupedItems[cat._id].filter((item) =>
              item.title.toLowerCase().includes(searchTerm.toLowerCase())
            );
            if (filteredItems.length === 0) return null;

            return (
              <div
                key={cat._id}
                ref={(el) => (categoryRefs.current[cat._id] = el)}
                data-category-id={cat._id}
                className="mb-6 scrollbar-thin"
              >
                <div className="flex justify-between my-3">
                  <h3 className="text-xl font-bold text-redlight mb-3 uppercase">
                    {cat.name}
                  </h3>
                  <div className="w-[50%] sm:max-w-full max-w-[50%]">
                    <img
                      src={cat.image}
                      alt={cat.name}
                      className="w-full object-cover mx-auto sm:max-h-[70px] lg:max-h-[150px]"
                    />
                  </div>
                </div>
                {filteredItems.map((item) => {
                  const isSelected = selectedValues.some(
                    (v) => v._id === item._id
                  );

                  return item && item._id ? (
                    <div key={item._id} className="mb-4">
                      <MenuItemDirect
                        item={item}
                        isSelected={isSelected}
                        onClick={() => {
                          setModalItem(item);
                        }}
                        operatorId={operatorId}
                        operatorType={operatorType}
                        operatorSlug={operatorSlug}
                      />
                    </div>
                  ) : null;
                })}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default MenuListDirect;
